import type { PropsWithChildren } from "react"
import React, { useEffect } from "react"
// TODO move to @mallardbay/lib-react-components
// eslint-disable-next-line no-restricted-imports
import { useMatch } from "@reach/router"
import {
    componentStyles,
    Box,
    VerticalStack,
} from "@mallardbay/lib-react-components"

import useAnalytics from "~utils/hooks/analytics-hooks/use-analytics"
import Header from "~components/shared/header/header"
import OutfitterAssistant from "~components/outfitter-assistant/outfitter-assistant"
import useOnboardingRedirects from "~utils/hooks/use-onboarding-redirects"
import { SCROLLABLE_CONTAINER } from "~config/constants"
import { ROUTES } from "~utils/helpers/navigation-helpers"
import Sidebar from "~components/shared/sidebar/sidebar"
import useSidebarInlineWidth from "~components/shared/sidebar/use-sidebar-inline-width"
import { useInAppBrowserContext } from "~contexts/in-app-browser-provider"
import { useOutfitterAssistant } from "~contexts/outfitter-assistant-provider"

import useCollapseSidebarWhenAssistantOpens from "./use-collapse-sidebar-when-assistant-opens"

export default function PageLayout({ children }: PropsWithChildren) {
    const doesNeedFullScreen = useDoesItNeedFullScreen()
    const { isInAppBrowser } = useInAppBrowserContext()
    const { close: closeOutfitterAssistant, isOpen: isOutfitterAssistantOpen } =
        useOutfitterAssistant()

    const styles = useStyles()

    useAnalytics()

    const { isRedirecting } = useOnboardingRedirects()

    useCollapseSidebarWhenAssistantOpens(isOutfitterAssistantOpen)

    useEffect(() => {
        if (doesNeedFullScreen) {
            closeOutfitterAssistant()
        }
    }, [closeOutfitterAssistant, doesNeedFullScreen])

    // Prevents the screen from flickering durect redirects.
    if (isRedirecting) return null

    // Embedded webview (e.g. mobile app) hides nav chrome so the host app
    // can render its own header/navigation around the dashboard. We still
    // need a scrollable container so dashboard pages behave normally.
    if (isInAppBrowser) {
        return (
            <Box id={SCROLLABLE_CONTAINER} style={styles.embeddedContainer}>
                {children}
            </Box>
        )
    }

    // Handle routes that need full screen
    if (doesNeedFullScreen) {
        return <Box style={styles.fullScreenContainer}>{children}</Box>
    }

    return (
        <Box style={styles.pageLayoutContainer}>
            <Sidebar />
            <Box style={styles.bodyContainer}>
                <Box style={styles.mainColumn}>
                    <VerticalStack style={styles.verticalStack}>
                        <Header />
                        <Box
                            id={SCROLLABLE_CONTAINER}
                            style={styles.bodyContent}
                        >
                            {children}
                        </Box>
                    </VerticalStack>
                </Box>
                <OutfitterAssistant />
            </Box>
        </Box>
    )
}

function useDoesItNeedFullScreen() {
    const isTeamMemberOnboarding = useMatch(ROUTES.TEAM_MEMBER_ONBOARDING)
    const isOnboardingRoute = useMatch(ROUTES.ONBOARD)
    const isBilling = useMatch(ROUTES.BILLING)
    const isBillingPortal = useMatch(ROUTES.BILLING_PORTAL)
    const isAccountPortal = useMatch(ROUTES.ACCOUNT_PORTAL)
    const isAppPromptBlocking = useMatch(ROUTES.APP_PROMPT_BLOCKING)
    const isCreateListing = useMatch(ROUTES.CREATE_LISTING)

    return (
        isTeamMemberOnboarding ||
        isOnboardingRoute ||
        isBilling ||
        isBillingPortal ||
        isAccountPortal ||
        isAppPromptBlocking ||
        isCreateListing
    )
}

function useStyles() {
    const sidebarWidth = useSidebarInlineWidth()

    const bodywidth = `calc(100% - ${sidebarWidth})`

    return componentStyles({
        pageLayoutContainer: {
            width: "100vw",
            height: "100vh",
            position: "absolute",
            display: "flex",
        },
        bodyContainer: {
            height: "100%",
            width: bodywidth,
            overflowX: "hidden",
            display: "flex",
            flexDirection: "row",
        },
        mainColumn: {
            flex: 1,
            minWidth: 0,
            height: "100%",
        },
        verticalStack: {
            height: "100%",
            overflowY: "hidden",
            gap: 0, //Override the lib default otherwise we end up with extra padding between header and content
        },
        fullScreenContainer: {
            width: "100vw",
            height: "100vh",
        },
        embeddedContainer: {
            width: "100vw",
            height: "100vh",
            overflowY: "scroll",
            overflowX: "hidden",
        },
        bodyContent: {
            height: "100%",
            width: "100%",
            overflowY: "scroll",
        },
    })
}
